import type { Company, ThemeClasses } from "../types";
import { CompanyGridCard } from "./CompanyGridCard";
import { CompanyListRow } from "./CompanyListRow";

interface CompanyListProps {
  companies: Company[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedView: "grid" | "list";
  onDetails: (company: Company) => void;
  onEdit: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onActivate?: (company: Company) => void;
  onSoftDelete?: (company: Company) => void;
  onSources?: (company: Company) => void;
  onRetransmissions?: (company: Company) => void;
  t: (key: string) => string;
  isSelectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelection?: (company: Company) => void;
}

export function CompanyList({
  companies,
  themeClasses,
  isDarkMode,
  selectedView,
  onDetails,
  onEdit,
  onDelete,
  onActivate,
  onSoftDelete,
  onSources,
  onRetransmissions,
  t,
  isSelectionMode = false,
  selectedIds = new Set(),
  onToggleSelection,
}: CompanyListProps) {
  if (companies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
            isDarkMode ? "bg-dark-card" : "bg-gray-100"
          }`}
        >
          <i
            className={`ri-building-2-line text-4xl ${themeClasses.textSecondary}`}
          ></i>
        </div>
        <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>
          {t("companies.noCompanies")}
        </h3>
        <p className={`text-sm ${themeClasses.textSecondary}`}>
          {t("companies.tryAdjustingFilters")}
        </p>
      </div>
    );
  }

  if (selectedView === "grid") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map((company) => (
          <CompanyGridCard
            key={company.id}
            company={company}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onDetails={onDetails}
            onEdit={onEdit}
            onDelete={onDelete}
            onActivate={onActivate}
            onSoftDelete={onSoftDelete}
            onSources={onSources}
            onRetransmissions={onRetransmissions}
            t={t}
            isSelectionMode={isSelectionMode}
            isSelected={selectedIds.has(company.id)}
            onToggleSelection={onToggleSelection}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {companies.map((company) => (
        <CompanyListRow
          key={company.id}
          company={company}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onDetails={onDetails}
          onEdit={onEdit}
          onDelete={onDelete}
          onActivate={onActivate}
          onSoftDelete={onSoftDelete}
          onSources={onSources}
          onRetransmissions={onRetransmissions}
          isSelectionMode={isSelectionMode}
          isSelected={selectedIds.has(company.id)}
          onToggleSelection={onToggleSelection}
        />
      ))}
    </div>
  );
}
